import { ArrowLeftIcon } from "@phosphor-icons/react/dist/csr/ArrowLeft";
import { InfoIcon } from "@phosphor-icons/react/dist/csr/Info";
import { Formik, Form } from "formik";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { Button } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import { FormInput } from "@volca/ui/components/ui/form-input";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { toast } from "@volca/ui/lib/toast";
import {
  isPasswordResetThrottledError,
  showThrottledResetToast,
  useAuth,
} from "@/core/auth";

const signupSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
});

export default function Signup() {
  const { forgotPassword, openResetCodeEntry } = useAuth();
  const navigate = useNavigate();

  return (
    <Stack
      gap={4}
      direction="row"
      align="center"
      justify="center"
      className="min-h-full w-full overflow-hidden p-6"
    >
      <Card className="w-full max-w-md gap-0 p-6 pt-8">
        <Formik
          initialValues={{ email: "" }}
          validationSchema={signupSchema}
          validateOnMount
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await forgotPassword(values);
              toast.success("Verification code sent");
            } catch (error) {
              if (isPasswordResetThrottledError(error)) {
                showThrottledResetToast(error.message, () =>
                  openResetCodeEntry(values.email),
                );
                return;
              }
              toast.error("Couldn't sign you up", {
                description:
                  "Please make sure your organization admin has created your account, and you're using the same email as in your welcome email.",
              });
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ getFieldProps, isSubmitting, isValid }) => (
            <Form>
              <Stack className="w-full" align="start" gap={6}>
                <Text variant="title" render={<h1 />} className="mt-4">
                  Sign up for your account
                </Text>

                <Stack direction="row" align="center" gap={1}>
                  <Text tone="muted">
                    We'll send you a code to set your password if an
                    administrator has created your account.
                  </Text>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <InfoIcon className="h-5 w-5 min-w-5 text-subtle" />
                      }
                    />
                    <TooltipContent className="max-w-md">
                      Make sure to use the same email that your organization
                      admin used when creating your account. This is likely the
                      same email you use to sign in to your work account.
                    </TooltipContent>
                  </Tooltip>
                </Stack>

                <FormInput
                  id="email"
                  type="email"
                  label="Email"
                  placeholder="Enter your email"
                  {...getFieldProps("email")}
                  fullWidth
                />

                <Stack className="w-full" align="start" gap={2}>
                  <Button
                    type="submit"
                    size="lg"
                    disabled={!isValid || isSubmitting}
                    className="w-full"
                    loading={isSubmitting}
                  >
                    Continue
                  </Button>

                  <Button
                    type="button"
                    size="lg"
                    variant="ghost"
                    onClick={() => navigate("/login")}
                    className="w-full"
                  >
                    <ArrowLeftIcon data-icon="inline-start" />
                    Back to sign in
                  </Button>
                </Stack>
              </Stack>
            </Form>
          )}
        </Formik>
      </Card>
    </Stack>
  );
}
